import React from "react";
import { motion } from "framer-motion";

/**
 * Coly mascot — custom SVG, drawn from scratch to match the reference design.
 * Multiple poses based on `mood`.
 */
export const ColyMascot = ({
  size = 200,
  mood = "good",
  bodyColor = "#6FB089",
  withSparkles = true,
  className = "",
  testId = "coly-mascot",
  animate = true,
  ...rest
}) => {
  const variants = animate
    ? {
        animate: { y: [0, -6, 0], scaleY: [1, 1.02, 1] },
        transition: { duration: 4.2, repeat: Infinity, ease: "easeInOut" },
      }
    : {};

  return (
    <motion.div
      data-testid={testId}
      className={`inline-block ${className}`}
      style={{ width: size, height: size }}
      animate={variants.animate}
      transition={variants.transition}
      {...rest}
    >
      <svg viewBox="0 0 100 100" width={size} height={size}>
        {/* Floor shadow */}
        <ellipse cx="50" cy="92" rx="28" ry="3.5" fill="rgba(0,0,0,0.12)" />

        {/* Legs */}
        <rect x="38" y="78" width="7" height="10" rx="3.5" fill={bodyColor} />
        <rect x="55" y="78" width="7" height="10" rx="3.5" fill={bodyColor} />

        {/* Feet */}
        <ellipse cx="41.5" cy="89" rx="6" ry="2.5" fill="#2F3A2C" opacity="0.85" />
        <ellipse cx="58.5" cy="89" rx="6" ry="2.5" fill="#2F3A2C" opacity="0.85" />

        {/* Body */}
        <ellipse cx="50" cy="50" rx="34" ry="32" fill={bodyColor} />
        <ellipse cx="50" cy="70" rx="2" ry="1.2" fill="#2F3A2C" opacity="0.25" />

        {/* Arms */}
        <ellipse cx="14" cy="52" rx="6" ry="4" fill={bodyColor} transform="rotate(-15 14 52)" />
        <ellipse cx="86" cy="52" rx="6" ry="4" fill={bodyColor} transform="rotate(15 86 52)" />

        {/* Cheeks */}
        <ellipse cx="32" cy="55" rx="4.5" ry="2.8" fill="#F4A28C" opacity="0.7" />
        <ellipse cx="68" cy="55" rx="4.5" ry="2.8" fill="#F4A28C" opacity="0.7" />

        {/* Eyes & mouth — varies per mood */}
        <Face mood={mood} />

        {/* Sparkles */}
        {withSparkles && (
          <g fill="#F4C95D">
            <path d="M16 24 l1.5 3 l3 1.5 l-3 1.5 l-1.5 3 l-1.5 -3 l-3 -1.5 l3 -1.5 z" />
            <path d="M82 22 l1.2 2.5 l2.5 1.2 l-2.5 1.2 l-1.2 2.5 l-1.2 -2.5 l-2.5 -1.2 l2.5 -1.2 z" />
          </g>
        )}
      </svg>
    </motion.div>
  );
};

const Face = ({ mood }) => {
  switch (mood) {
    case "good":
      return (
        <>
          <g fill="none" stroke="#2F3A2C" strokeWidth="2.4" strokeLinecap="round">
            <path d="M33 47 Q38 41 43 47" />
            <path d="M57 47 Q62 41 67 47" />
          </g>
          <path d="M42 58 Q50 70 58 58 Z" fill="#2F3A2C" />
        </>
      );
    case "quiet":
      return (
        <>
          <g fill="#2F3A2C">
            <circle cx="38" cy="46" r="3" />
            <circle cx="62" cy="46" r="3" />
            <circle cx="38.8" cy="45.2" r="0.9" fill="#fff" />
            <circle cx="62.8" cy="45.2" r="0.9" fill="#fff" />
          </g>
          <path
            d="M44 60 Q50 63 56 60"
            fill="none"
            stroke="#2F3A2C"
            strokeWidth="2.4"
            strokeLinecap="round"
          />
        </>
      );
    case "meh":
      return (
        <>
          <g fill="#2F3A2C">
            <ellipse cx="38" cy="46" rx="3.4" ry="2.6" />
            <ellipse cx="62" cy="46" rx="3.4" ry="2.6" />
          </g>
          <line
            x1="44"
            y1="60"
            x2="56"
            y2="60"
            stroke="#2F3A2C"
            strokeWidth="2.4"
            strokeLinecap="round"
          />
        </>
      );
    case "tough":
      return (
        <>
          <g fill="none" stroke="#2F3A2C" strokeWidth="2.4" strokeLinecap="round">
            <path d="M33 49 Q38 45 43 49" />
            <path d="M57 49 Q62 45 67 49" />
          </g>
          <path
            d="M43 62 Q50 56 57 62"
            fill="none"
            stroke="#2F3A2C"
            strokeWidth="2.4"
            strokeLinecap="round"
          />
        </>
      );
    case "flare":
      return (
        <>
          <g fill="none" stroke="#2F3A2C" strokeWidth="2.4" strokeLinecap="round">
            <path d="M33 48 L43 44" />
            <path d="M57 44 L67 48" />
          </g>
          <ellipse cx="50" cy="62" rx="5" ry="3.5" fill="#2F3A2C" />
        </>
      );
    case "streak":
      return (
        <>
          <g fill="none" stroke="#2F3A2C" strokeWidth="2.4" strokeLinecap="round">
            <path d="M32 47 Q38 41 44 47" />
            <path d="M56 47 Q62 41 68 47" />
          </g>
          <path d="M40 58 Q50 72 60 58 Z" fill="#2F3A2C" />
          <path d="M47 64 L53 64 Q53 68 50 68 Q47 68 47 64" fill="#F4A28C" />
        </>
      );
    case "resting":
      return (
        <>
          <g fill="none" stroke="#2F3A2C" strokeWidth="2.4" strokeLinecap="round">
            <path d="M33 47 Q38 50 43 47" />
            <path d="M57 47 Q62 50 67 47" />
          </g>
          <path
            d="M44 60 Q50 62 56 60"
            fill="none"
            stroke="#2F3A2C"
            strokeWidth="2.4"
            strokeLinecap="round"
          />
          <text
            x="74"
            y="32"
            fontFamily="Nunito"
            fontWeight="900"
            fontSize="10"
            fill="#7A8576"
          >
            z
          </text>
          <text
            x="80"
            y="24"
            fontFamily="Nunito"
            fontWeight="900"
            fontSize="7"
            fill="#7A8576"
          >
            z
          </text>
        </>
      );
    default:
      return null;
  }
};

/* Tiny avatar (nav etc) */
export const ColyAvatar = ({ size = 36, className = "" }) => (
  <ColyMascot size={size} mood="good" withSparkles={false} animate={false} className={className} />
);

export default ColyMascot;
